// apiFreshness.js — v0.172.0 — knowing when a cached answer is too old.
// ---------------------------------------------------------------------------
// Brett, 2026-09-14: "after i load some pins they dont appear on the defects
// list straight away so i stand there thinking its not working."
//
// THE CAUSE. Since v0.65 the service worker answers every /api/* GET
// stale-while-revalidate: the cached copy comes back instantly and the cache
// is refreshed in the background. That is right for a plan opening on one
// bar of reception, and wrong the moment the app itself has changed the data
// — the register asked for the pin list, got the copy cached BEFORE the pins
// were dropped, and nothing ever told it the fresh copy had arrived.
//
// TWO FIXES, BOTH HERE:
//   1. The app remembers when it last CHANGED anything (a save that went
//      straight up, or an outbox row that replayed). A GET whose last fresh
//      answer predates that change asks the service worker for the network
//      first (X-DropPin-Fresh header), so a screen opened after a save shows
//      the save.
//   2. When the service worker's background refresh finds the data has
//      changed, it messages every open page (API_UPDATED); screens that show
//      that data reload. Covers changes made elsewhere — another device, the
//      office — arriving while you look.
//
// Pure bookkeeping, no fetch of its own, so it can sit under api.js and the
// outbox without a cycle and be tested in node.

let _mutatedAt = 0;
const _freshAt = new Map();     // path → when the network last answered it
const _listeners = new Set();

export function markApiMutated(now) { _mutatedAt = Number.isFinite(now) ? now : Date.now(); }
export function noteFresh(path, now) { _freshAt.set(String(path), Number.isFinite(now) ? now : Date.now()); }

// Should this GET skip the cached copy and ask the network first?
export function needsFresh(path) {
  if (!_mutatedAt) return false;
  return (_freshAt.get(String(path)) || 0) < _mutatedAt;
}

export function subscribeApiUpdates(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// v0.173.0 — HARD STOP against a reload loop. Whatever the service worker
// decides is "changed", the page acts on an update for a given URL at most
// once per UPDATE_COOLDOWN_MS. A screen reloading in response to its own
// reload's refresh can therefore never run away, even if the comparison in
// sw.js is wrong again some day.
const UPDATE_COOLDOWN_MS = 10000;
const _lastUpdate = new Map();   // url (no query) → when we last acted on it
export function notifyApiUpdated(url, now) {
  const u = String(url || '');
  const key = u.split('?')[0];
  const t = Number.isFinite(now) ? now : Date.now();
  const last = _lastUpdate.get(key) || 0;
  if (last && t - last < UPDATE_COOLDOWN_MS) return false;
  _lastUpdate.set(key, t);
  _listeners.forEach((fn) => { try { fn(u); } catch (_e) { /* listener's problem */ } });
  return true;
}

// Wire the service worker's API_UPDATED messages to the subscribers above.
// Safe to call more than once; a no-op without a service worker.
let _wired = false;
export function listenToServiceWorker() {
  if (_wired) return;
  if (typeof navigator === 'undefined' || !navigator.serviceWorker) return;
  _wired = true;
  navigator.serviceWorker.addEventListener('message', (e) => {
    const d = e && e.data;
    if (d && d.type === 'API_UPDATED' && d.url) notifyApiUpdated(d.url);
  });
}

// Does an updated URL concern this screen's data? A pin change touches the
// pin list AND the single-pin endpoint; a photo change touches the pin
// (photoCount / preview) too.
export function urlConcerns(url, resources) {
  const path = String(url || '').replace(/^https?:\/\/[^/]+/, '').split('?')[0];
  const seg = path.split('/').filter(Boolean).filter(s => s !== 'api')[0] || '';
  return (resources || []).includes(seg);
}

// Test hook.
export function _resetFreshness() { _mutatedAt = 0; _freshAt.clear(); _listeners.clear(); _lastUpdate.clear(); }
